import type {
  StoryboardSegment,
  StoryboardSegmentTask,
  StoryboardSegmentTaskStatus,
} from '../../types';

type ChipColor = 'default' | 'info' | 'warning' | 'success' | 'error'
type ChipVariant = 'outlined' | 'filled'

export const clampProgress = (value?: number): number | undefined => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined;
  }
  if (value <= 0) return 0;
  if (value >= 100) return 100;
  return Math.round(value);
};

export const buildStatusChipProps = (
  segment: StoryboardSegment
): { label: string; color: ChipColor; variant: ChipVariant } => {
  const progress = clampProgress(segment.progress);
  switch (segment.status) {
    case 'queued':
      return {
        label: progress !== undefined ? `排队中 · ${progress}%` : '排队中',
        color: 'info',
        variant: 'outlined',
      };
    case 'generating':
      return {
        label: progress !== undefined ? `生成中 · ${progress}%` : '生成中',
        color: 'warning',
        variant: 'filled',
      };
    case 'success':
      return {
        label: '已完成',
        color: 'success',
        variant: 'filled',
      };
    case 'failed':
      return {
        label: '失败',
        color: 'error',
        variant: 'filled',
      };
    case 'draft':
      return {
        label: '草稿',
        color: 'default',
        variant: 'outlined',
      };
    case 'idle':
    default:
      return {
        label: '未开始',
        color: 'default',
        variant: 'outlined',
      };
  }
};

export const buildTaskStatusChipProps = (
  status: StoryboardSegmentTaskStatus
): { label: string; color: ChipColor; variant: ChipVariant } => {
  switch (status) {
    case 'queued':
      return { label: '排队中', color: 'info', variant: 'outlined' };
    case 'generating':
      return { label: '生成中', color: 'warning', variant: 'filled' };
    case 'success':
      return { label: '已完成', color: 'success', variant: 'filled' };
    case 'failed':
      return { label: '失败', color: 'error', variant: 'filled' };
    default:
      return { label: '未知状态', color: 'default', variant: 'outlined' };
  }
};

export const buildTaskStatusDescription = (
  task: StoryboardSegmentTask,
  progress?: number
): string => {
  switch (task.status) {
    case 'queued':
      return progress !== undefined ? `排队中 · ${progress}%` : '排队中，等待任务开始';
    case 'generating':
      return progress !== undefined ? `生成中 · ${progress}%` : '生成中';
    case 'success':
      return '已完成';
    case 'failed':
      return task.failReason ? `失败 · ${task.failReason}` : '任务失败';
    default:
      return '状态未知';
  }
};
